import React, { useEffect, useState } from 'react';
import { fetchOvrLogs } from '../api/api';

const AuditView = () => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [allLogs, setAllLogs] = useState({ items: [] }); // All loaded logs
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Search states for querying specific items
  const [searchFilename, setSearchFilename] = useState('');
  const [searchFilenameValue, setSearchFilenameValue] = useState('');
  const [startTimestamp, setStartTimestamp] = useState('');
  const [endTimestamp, setEndTimestamp] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState({ items: [] });
  
  // UI filter states (for filtering loaded data)
  const [filters, setFilters] = useState({
    username: '',
    campaign: '',
    template: ''
  });
  
  // Get unique values for filter dropdowns
  const getUniqueValues = (key) => {
    const sourceItems = isSearchMode ? searchResults.items : allLogs.items;
    const values = sourceItems
      .map(item => item[key])
      .filter(value => value && value !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return values;
  };
  
  // Get filtered items based on current mode and UI filters
  const getFilteredItems = () => {
    const sourceItems = isSearchMode ? searchResults.items : allLogs.items;
    
    return sourceItems.filter(item => {
      const matchesUsername = !filters.username || 
        (item.email && item.email.toLowerCase().includes(filters.username.toLowerCase()));
      
      const matchesCampaign = !filters.campaign || 
        (item.campaign && item.campaign === filters.campaign);
      
      const matchesTemplate = !filters.template || 
        (item.template && item.template === filters.template);
      
      return matchesUsername && matchesCampaign && matchesTemplate;
    });
  };

  const loadMoreLogs = async (isLoadMore = false, lastKey = null) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Ensure lastKey is properly stringified if it's an object
      let lastKeyParam = null;
      if (lastKey) {
        lastKeyParam = typeof lastKey === 'object' ? JSON.stringify(lastKey) : lastKey;
      }

      // Call with standard parameters - no filename for regular load
      const res = await fetchOvrLogs(lastKeyParam, 20, null, null, null);
        
      console.log('Logs : ', res);
      
      if (isLoadMore) {
        // Append new items to existing ones
        setAllLogs(prev => ({
          ...res,
          items: [...prev.items, ...res.items]
        }));
      } else {
        // Replace with new data (initial load)
        setAllLogs(res);
      }

      // Update pagination state
      setLastEvaluatedKey(res.lastEvaluatedKey || null);
      setHasMoreData(!!res.lastEvaluatedKey);

    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const searchByFilename = async (filename, startTime = null, endTime = null) => {
    try {
      setLoading(true);
      setIsSearchMode(true);
      
      // Use the fetchOvrLogs function with all parameters
      const res = await fetchOvrLogs(null, 100, filename, startTime, endTime);
      console.log('Search results: ', res);
      
      setSearchResults(res);
      
    } catch (error) {
      console.error('Error searching logs:', error);
      setSearchResults({ items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMoreLogs();
  }, []);

  const handleLoadMore = () => {
    if (!loadingMore && hasMoreData && !isSearchMode) {
      loadMoreLogs(true, lastEvaluatedKey);
    }
  };

  const handleFilenameSearch = (e) => {
    e.preventDefault();
    if (searchFilenameValue.trim()) {
      searchByFilename(
        searchFilenameValue.trim(), 
        startTimestamp || null, 
        endTimestamp || null
      );
      setSearchFilename(searchFilenameValue.trim());
    }
  };

  const clearSearch = () => {
    setSearchFilename('');
    setSearchFilenameValue('');
    setStartTimestamp('');
    setEndTimestamp('');
    setIsSearchMode(false);
    setSearchResults({ items: [] });
    // Reload all logs after clearing search
    loadMoreLogs();
  };

  const clearFilters = () => {
    setFilters({
      username: '',
      campaign: '',
      template: ''
    });
  };

  const hasActiveFilters = filters.username || filters.campaign || filters.template;
  const filteredItems = getFilteredItems();

  const parseJSONString = (jsonStr) => {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      return { error: "Invalid JSON", raw: jsonStr };
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'passed': 'bg-green-100 text-green-800 border-green-200',
      'failed': 'bg-red-100 text-red-800 border-red-200',
      'success': 'bg-green-100 text-green-800 border-green-200',
      'error': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getAnswerColor = (answer, type) => {
    // For Compliance: "no" = green (compliant), "yes" = red (violation)
    // For Quality: "yes" = green (pass), "no" = red (fail)
    const isCompliance = type === 'Compliance';

    if (answer?.toLowerCase() === 'yes') {
      return isCompliance
        ? 'bg-red-100 text-red-800 border-red-200'
        : 'bg-green-100 text-green-800 border-green-200';
    } else if (answer?.toLowerCase() === 'no') {
      return isCompliance
        ? 'bg-green-100 text-green-800 border-green-200'
        : 'bg-red-100 text-red-800 border-red-200';
    }

    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const detectChanges = (oldValue, newValue) => {
    const oldParsed = parseJSONString(oldValue);
    const newParsed = parseJSONString(newValue);

    const changes = [];

    if (oldParsed.status !== newParsed.status) {
      changes.push('status');
    }

    if (oldParsed.score !== newParsed.score) {
      changes.push('score');
    }

    if (oldParsed.answer !== newParsed.answer) {
      changes.push('answer');
    }

    if (oldParsed.reason !== newParsed.reason) {
      changes.push('reason');
    }

    return changes;
  };

  const FieldComparison = ({ fieldName, oldValue, newValue, isOld = false }) => {
    const parsed = parseJSONString(isOld ? oldValue : newValue);
    const changes = detectChanges(oldValue, newValue);
    const isChanged = changes.length > 0;

    const borderClass = isChanged ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-white';

    // Check if specific fields changed
    const statusChanged = changes.includes('status');
    const scoreChanged = changes.includes('score');
    const answerChanged = changes.includes('answer');
    const reasonChanged = changes.includes('reason');

    return (
      <div className={`border rounded-lg p-4 ${borderClass}`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800 capitalize">{fieldName}</h4>
          {isChanged && (
            <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
              Changed
            </span>
          )}
        </div>

        {parsed.status && (
          <div className={`mb-2 ${statusChanged ? 'bg-yellow-50 p-2 rounded border border-yellow-200' : ''}`}>
            <span className="text-xs font-medium text-gray-600">Status:</span>
            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(parsed.status)}`}>
              {parsed.status}
            </span>
          </div>
        )}

        {parsed.answer && (
          <div className={`mb-2 ${answerChanged ? 'bg-yellow-50 p-2 rounded border border-yellow-200' : ''}`}>
            <span className="text-xs font-medium text-gray-600">Answer:</span>
            <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium border ${getAnswerColor(parsed.answer, parsed.type)}`}>
              {parsed.answer.toUpperCase()}
            </span>
            {parsed.type && (
              <span className="ml-2 text-xs text-gray-500">({parsed.type})</span>
            )}
          </div>
        )}

        {parsed.score && (
          <div className={`mb-2 ${scoreChanged ? 'bg-yellow-50 p-2 rounded border border-yellow-200' : ''}`}>
            <span className="text-xs font-medium text-gray-600">Score:</span>
            <span className="ml-2 font-medium text-blue-600">{parsed.score}</span>
          </div>
        )}

        {parsed.reason && (
          <div className={`mb-2 ${reasonChanged ? 'bg-yellow-50 p-2 rounded border border-yellow-200' : ''}`}>
            <span className="text-xs font-medium text-gray-600">Reason:</span>
            <p className="text-sm text-gray-700 mt-1">{parsed.reason}</p>
          </div>
        )}

        {parsed.evidence && (
          <div className="mt-3">
            <span className="text-xs font-medium text-gray-600">Evidence:</span>
            <div className="mt-1 space-y-1">
              {parsed.evidence.agent && (
                <div className="text-xs">
                  <strong>Agent:</strong> "{parsed.evidence.agent}"
                </div>
              )}
              {parsed.evidence.customer && (
                <div className="text-xs">
                  <strong>Customer:</strong> "{parsed.evidence.customer}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const ItemCard = ({ item, onClick }) => {
    const totalChanges = Object.keys(item.old || {}).reduce((count, key) => {
      const changes = detectChanges(item.old[key], item.new?.[key] || '');
      return count + changes.length;
    }, 0);

    const nameFormat = (filename) => {
      if (filename && filename.length >= 60) {
        return filename.slice(0, 60) + '...';
      }
      return filename;
    };

    return (
      <div 
        className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 hover:border-blue-300"
        onClick={onClick}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-800 truncate">
                  {nameFormat(item.filename)}
                </h3>
                <p className="text-sm text-gray-600 truncate">{item.email}</p>
                <div className="flex gap-4 mt-1">
                  {item.campaign && (
                    <p className="text-xs text-gray-500">Campaign: <span className="font-medium">{item.campaign}</span></p>
                  )}
                  {item.template && (
                    <p className="text-xs text-gray-500">Template: <span className="font-medium">{item.template}</span></p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-500">{formatTimestamp(item.updatedAt)}</p>
              {totalChanges > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                  {totalChanges} changes
                </span>
              )}
            </div>
          </div>
          
          {item.old && (
            <div className="flex items-center text-sm text-gray-600">
              <span className="mr-2">Fields:</span>
              <div className="flex flex-wrap gap-2">
                {Object.keys(item.old).map(key => (
                  <span key={key} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs capitalize">
                    {key}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
            <span>Click to view changes</span>
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  };

  const Modal = ({ item, onClose }) => {
    if (!item) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 break-words">{item.filename}</h2>
              <p className="text-sm text-gray-600">{item.email} • {formatTimestamp(item.updatedAt)}</p>
              {item.campaign && <p className="text-xs text-gray-500">Campaign: {item.campaign}</p>}
              {item.template && <p className="text-xs text-gray-500">Template: {item.template}</p>}
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {item.old && item.new ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Old Values */}
                <div>
                  <h3 className="text-lg font-semibold text-red-600 mb-4 flex items-center">
                    <span className="w-4 h-4 bg-red-600 rounded-full mr-2"></span>
                    Old Values
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(item.old).map(([key, value]) => (
                      <FieldComparison
                        key={`old-${key}`}
                        fieldName={key}
                        oldValue={value}
                        newValue={item.new[key] || ''}
                        isOld={true}
                      />
                    ))}
                  </div>
                </div>
                
                {/* New Values */}
                <div>
                  <h3 className="text-lg font-semibold text-green-600 mb-4 flex items-center">
                    <span className="w-4 h-4 bg-green-600 rounded-full mr-2"></span>
                    New Values
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(item.new).map(([key, value]) => (
                      <FieldComparison
                        key={`new-${key}`}
                        fieldName={key}
                        oldValue={item.old[key] || ''}
                        newValue={value}
                        isOld={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No change data available for this item.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="bg-gray-100 min-h-screen">
      {loading && !isSearchMode && allLogs.items.length === 0 ? (
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <LoadingSpinner />
            <h1 className="text-xl text-gray-600 mt-4">Loading Override Logs...</h1>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Override Logs Dashboard</h1>
          
          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-6">
            {/* Query Specific Items Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                Query Specific Item
              </h3>
              <form onSubmit={handleFilenameSearch}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filename <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter exact filename..."
                      value={searchFilenameValue}
                      onChange={(e) => setSearchFilenameValue(e.target.value)}
                      className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Timestamp
                      </label>
                      <input
                        type="datetime-local"
                        value={startTimestamp}
                        onChange={(e) => setStartTimestamp(e.target.value)}
                        className="w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Timestamp
                      </label>
                      <input
                        type="datetime-local"
                        value={endTimestamp}
                        onChange={(e) => setEndTimestamp(e.target.value)}
                        className="w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    disabled={loading || !searchFilenameValue.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    {loading ? 'Searching...' : 'Query Item'}
                  </button>
                  
                  {isSearchMode && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    >
                      Clear Query
                    </button>
                  )}
                </div>
              </form>
            </div>
            
            {/* UI Filter Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                </svg>
                Filter Loaded Items
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username (Email)</label>
                  <input
                    type="text"
                    placeholder="Filter by email..."
                    value={filters.username}
                    onChange={(e) => setFilters({...filters, username: e.target.value})}
                    className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
                  <select
                    value={filters.campaign}
                    onChange={(e) => setFilters({...filters, campaign: e.target.value})}
                    className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  >
                    <option value="">All Campaigns</option>
                    {getUniqueValues('campaign').map(campaign => (
                      <option key={campaign} value={campaign}>{campaign}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
                  <select
                    value={filters.template}
                    onChange={(e) => setFilters({...filters, template: e.target.value})}
                    className="w-full px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  >
                    <option value="">All Templates</option>
                    {getUniqueValues('template').map(template => (
                      <option key={template} value={template}>{template}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {hasActiveFilters && (
                <div className="mt-4">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Status Information */}
          <div className="mb-4 flex flex-wrap gap-4 text-sm">
            {isSearchMode ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  Query Results: {searchFilename}
                </span>
                <span className="text-gray-600">({filteredItems.length} items)</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  All Logs
                </span>
                <span className="text-gray-600">
                  ({filteredItems.length} of {allLogs.items.length} shown)
                </span>
              </div>
            )}
            
            {/* Active Filters Display */}
            {filters.username && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Email: {filters.username}
              </span>
            )}
            {filters.campaign && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Campaign: {filters.campaign}
              </span>
            )}
            {filters.template && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Template: {filters.template}
              </span>
            )}
          </div>
          
          {/* Loading indicator for search */}
          {loading && isSearchMode && (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="text-gray-600 mt-2">Querying database...</p>
            </div>
          )}
          
          {/* Items List */}
          <div className="space-y-4">
            {filteredItems.map((item, index) => (
              <ItemCard
                key={`${item.filename}-${item.updatedAt}-${index}`}
                item={item}
                onClick={() => setExpandedItem(item)}
              />
            ))}
          </div>
          
          {/* Load More Button - only show when not in search mode */}
          {!isSearchMode && hasMoreData && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <span>Load More</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* No more data message */}
          {!isSearchMode && !hasMoreData && allLogs.items.length > 0 && (
            <div className="text-center mt-8 py-4">
              <p className="text-gray-500">No more logs to load</p>
            </div>
          )}
          
          {/* No results message */}
          {filteredItems.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">No results found</h3>
              <p className="text-gray-500">
                {isSearchMode 
                  ? "No logs found for the specified filename and timestamp range." 
                  : hasActiveFilters 
                    ? "No logs match the current filters."
                    : "No logs available."
                }
              </p>
            </div>
          )}
          
          {/* Modal */}
          {expandedItem && (
            <Modal
              item={expandedItem}
              onClose={() => setExpandedItem(null)}
            />
          )}
        </div>            
      )}
    </div>
  );
};

export default AuditView;